import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Box,
  IconButton,
  Menu,
  MenuItem,
  LinearProgress,
  Tooltip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  CloudOff as CloudOffIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { Auditoria } from '../../../types/audit.types';
import auditService from '../../../services/auditService';
import { formatDateTime } from '../../../utils/dateUtils';

interface AuditCardProps {
  auditoria: Auditoria;
  onClick: () => void;
  onDeleteSuccess: () => void;
}

const AuditCard: React.FC<AuditCardProps> = ({ auditoria, onClick, onDeleteSuccess }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const menuOpen = Boolean(anchorEl);

  // Handlers
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      await auditService.delete(auditoria.id);
      setDeleteDialogOpen(false);
      onDeleteSuccess();
    } catch (error) {
      console.error('Error eliminando auditoría:', error);
      alert('Error al eliminar la auditoría');
    } finally {
      setDeleting(false);
    }
  };

  // Formato de fecha
  const formatDate = (dateString: string) => {
    return formatDateTime(dateString, auditoria.business_timezone);
  };

  // Color del estado
  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'draft':
        return 'warning';
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'info';
      default:
        return 'default';
    }
  };

  // Icono del estado
  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'draft':
        return <EditIcon fontSize="small" />;
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'in_progress':
        return <ScheduleIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const progreso = auditoria.progress_percentage || 0;
  const questions_answered = auditoria.questions_answered || 0;
  const total_questions = auditoria.total_questions || 0;

  return (
    <>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8],
          },
          '&:active': {
            transform: 'translateY(-2px)',
          },
        }}
        onClick={onClick}
      >
        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
          {/* Header con estado */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Chip
              label={(auditoria as any).status}
              color={getEstadoColor((auditoria as any).status)}
              size="small"
              icon={getEstadoIcon((auditoria as any).status) || undefined}
              sx={{ textTransform: 'capitalize' }}
            />
            
            {!(auditoria as any).synchronized && (
              <Tooltip title="Pendiente de sincronización">
                <CloudOffIcon fontSize="small" color="warning" />
              </Tooltip>
            )}
          </Box>

          {/* Negocio */}
          <Typography variant="h6" component="h2" gutterBottom sx={{ 
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}>
            {(auditoria as any).business_name || 'Sin negocio'}
          </Typography>

          {/* Formulario */}
          <Typography variant="body2" color="text.secondary" gutterBottom>
            📋 {(auditoria as any).form_name || 'Sin formulario'}
          </Typography>

          {/* Auditor */}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            👤 {(auditoria as any).auditor_name || 'Sin auditor'}
          </Typography>

          {/* Progreso */}
          {(auditoria as any).status === 'draft' && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Progreso
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {questions_answered}/{total_questions} ({progreso}%)
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={Number(progreso)} 
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          )}

          {/* Puntaje (solo completadas) */}
          {(auditoria as any).status !== 'draft' && (auditoria as any).compliance_percentage !== null && (
            <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
              Cumplimiento: {(auditoria as any).compliance_percentage}%
            </Typography>
          )}

          {/* Fecha */}
          <Typography variant="caption" color="text.secondary" display="block">
            🗓️ Iniciada: {formatDate((auditoria as any).start_date)}
          </Typography>
          
          {auditoria.end_date && (
            <Typography variant="caption" color="text.secondary" display="block">
              ✅ Finalizada: {formatDate(auditoria.end_date)}
            </Typography>
          )}
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button
            size="small"
            startIcon={(auditoria as any).status === 'draft' ? <EditIcon /> : <VisibilityIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            {(auditoria as any).status === 'draft' ? 'Continuar' : 'Ver Detalle'}
          </Button>

          <IconButton
            size="small"
            onClick={handleMenuClick}
            disabled={(auditoria as any).status !== 'draft'}
          >
            <MoreVertIcon />
          </IconButton>
        </CardActions>
      </Card>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleDeleteClick}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>

      {/* Dialog de confirmación */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>¿Eliminar auditoría?</DialogTitle>
        <DialogContent>
          <Typography>
            Esta acción no se puede deshacer. Se eliminará la auditoría de "{(auditoria as any).business_name}" y todas sus respuestas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AuditCard;
